import React from 'react';
import { X, AlertCircle, CheckCircle, Clock, Ban, RefreshCw, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BookingTermsModal = ({ isOpen, onClose, onAccept, bookingDetails = {}, timingWarning = null }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{t('bookingTerms.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="close-terms-modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Timing Warning */}
          {timingWarning && timingWarning.show_warning && (
            <div className={`p-4 rounded-lg border-2 ${timingWarning.severity === 'error' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-6 h-6 flex-shrink-0 ${timingWarning.severity === 'error' ? 'text-red-600' : 'text-yellow-600'}`} />
                <div>
                  <h3 className={`font-semibold mb-2 ${timingWarning.severity === 'error' ? 'text-red-900' : 'text-yellow-900'}`}>
                    {t('bookingTerms.importantNotice')}
                  </h3>
                  <p className={`text-sm mb-2 ${timingWarning.severity === 'error' ? 'text-red-800' : 'text-yellow-800'}`}>
                    {timingWarning.message}
                  </p>
                  {timingWarning.restrictions && (
                    <ul className="text-sm space-y-1 mt-2">
                      {timingWarning.restrictions.map((restriction, idx) => (
                        <li key={idx} className={`flex items-center gap-2 ${timingWarning.severity === 'error' ? 'text-red-700' : 'text-yellow-700'}`}>
                          <Ban className="w-4 h-4" />
                          {restriction}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cancellation Policy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-blue-600">
              <Clock className="w-5 h-5" />
              <h3 className="font-semibold text-lg">{t('bookingTerms.cancellationPolicy')}</h3>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg space-y-2">
              {[
                { icon: CheckCircle, text: t('bookingTerms.freeCancellation') },
                { icon: Ban, text: t('bookingTerms.noCancelWithin24') },
                { icon: AlertCircle, text: t('bookingTerms.contactProvider') },
                { icon: CreditCard, text: t('bookingTerms.refundAsCredits') },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <item.icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-900">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Arrival Time Policy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-600">
              <Clock className="w-5 h-5" />
              <h3 className="font-semibold text-lg">{t('bookingTerms.arrivalPolicy')}</h3>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg space-y-2">
              {[
                { icon: CheckCircle, text: t('bookingTerms.arrive10Min') },
                { icon: AlertCircle, text: t('bookingTerms.late15Min') },
                { icon: Ban, text: t('bookingTerms.lateNoRefund') },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <item.icon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Rescheduling Policy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-purple-600">
              <RefreshCw className="w-5 h-5" />
              <h3 className="font-semibold text-lg">{t('bookingTerms.reschedulePolicy')}</h3>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg space-y-2">
              {[
                { icon: CheckCircle, text: t('bookingTerms.reschedule3Hours') },
                { icon: Ban, text: t('bookingTerms.noRescheduleAfter') },
                { icon: AlertCircle, text: t('bookingTerms.providerGracePeriod') },
                { icon: AlertCircle, text: t('bookingTerms.maxReschedules') },
                { icon: CheckCircle, text: t('bookingTerms.sameProvider') },
                { icon: AlertCircle, text: t('bookingTerms.priceDifferences') },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <item.icon className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-purple-900">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* No-Show Policy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              <h3 className="font-semibold text-lg">{t('bookingTerms.noShowPolicy')}</h3>
            </div>
            <div className="bg-red-50 p-4 rounded-lg space-y-2">
              {[
                { icon: AlertCircle, text: t('bookingTerms.noShowNonRefundable') },
                { icon: AlertCircle, text: t('bookingTerms.emergencyContact') },
                { icon: AlertCircle, text: t('bookingTerms.multipleNoShows') },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <item.icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-900">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Refund Policy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <CreditCard className="w-5 h-5" />
              <h3 className="font-semibold text-lg">{t('bookingTerms.refundPolicy')}</h3>
            </div>
            <div className="bg-green-50 p-4 rounded-lg space-y-2">
              {[
                { icon: CheckCircle, text: t('bookingTerms.refundsAsCredits') },
                { icon: CheckCircle, text: t('bookingTerms.creditsAnyProvider') },
                { icon: CheckCircle, text: t('bookingTerms.creditsNoExpire') },
                { icon: CheckCircle, text: t('bookingTerms.refundsImmediate') },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <item.icon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Agreement */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700">{t('bookingTerms.agreementText')}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex gap-3">
          <button onClick={onClose} className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium" data-testid="terms-cancel-btn">
            {t('bookingTerms.cancelBtn')}
          </button>
          <button onClick={onAccept} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-testid="terms-accept-btn">
            {t('bookingTerms.acceptBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingTermsModal;
